import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ModelType, IAudioGeneration, TaskStatus,
  CreateAudioEditingRequest, AudioGenerationQuery, PaginationQuery, PaginationResult,
} from '@packages/global';
import { audioEditingApi } from '../services/user/audioEditing.api';
import { useModels } from '../hooks/useModels';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType } from '../services/websocket/types';
import { MagnifyingGlassIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import AudioTabs from '../components/common/AudioTabs';
import AudioEditingForm, { AudioEditFormData, DEFAULT_AUDIO_EDIT_FORM } from '../components/AudioEditing/AudioEditingForm';
import AudioEditCard from '../components/AudioEditing/AudioEditCard';
import Pagination from '../components/Pagination';

export default function AudioEditsPage() {
  const { t } = useTranslation(['generation', 'common']);
  const [items, setItems] = useState<IAudioGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const { models, loading: modelsLoading } = useModels(ModelType.AUDIO_EDIT);
  const { alert, confirm } = useDialog();
  const { client, isConnected } = useWebSocketContext();

  const statusOptions = [
    { value: '', label: t('audioEdit.allStatus') },
    { value: TaskStatus.PENDING, label: t('audioEdit.statusPending') },
    { value: TaskStatus.SUBMITTED, label: t('audioEdit.statusSubmitted') },
    { value: TaskStatus.PROCESSING, label: t('audioEdit.statusProcessing') },
    { value: TaskStatus.COMPLETED, label: t('audioEdit.statusCompleted') },
    { value: TaskStatus.FAILED, label: t('audioEdit.statusFailed') },
  ];

  const [formData, setFormData] = useState<AudioEditFormData>(DEFAULT_AUDIO_EDIT_FORM);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1, limit: 24, total: 0, totalPages: 0,
    sortBy: 'createdAt', sortOrder: 'desc',
  });

  const loadItems = async (
    queryParams: Partial<AudioGenerationQuery> = {},
    page = 1,
    limit = 24
  ) => {
    try {
      const queryData: PaginationQuery<AudioGenerationQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          status: queryParams.status ?? (statusFilter || undefined),
          audioType: 'editing',
          ...queryParams,
        },
        pagination: { page, limit, sortBy: 'createdAt', sortOrder: 'desc' },
      };
      const response = await audioEditingApi.page(queryData);
      setItems(response.items);
      setPagination(prev => ({
        ...prev, page, limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      console.error('加载音频编辑任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // WebSocket 订阅
  useEffect(() => {
    if (!client || !isConnected) return;
    const unsubStatus = client.onMessage(MessageType.TASK_STATUS_CHANGED, (msg: TaskStatusChangedMessage) => {
      if (msg.taskType !== TaskType.AUDIO_EDITING) return;
      setItems(prev => {
        const idx = prev.findIndex(i => i.id === msg.taskId);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], status: msg.newStatus as unknown as TaskStatus };
        if (msg.newStatus === TaskStatus.COMPLETED || msg.newStatus === TaskStatus.FAILED) {
          audioEditingApi.get(msg.taskId)
            .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
            .catch(console.error);
        }
        return next;
      });
    });

    const unsubCompleted = client.onMessage(MessageType.TASK_COMPLETED, (msg: any) => {
      if (msg.taskType !== TaskType.AUDIO_EDITING) return;
      audioEditingApi.get(msg.taskId)
        .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
        .catch(console.error);
    });

    const unsubFailed = client.onMessage(MessageType.TASK_FAILED, (msg: any) => {
      if (msg.taskType !== TaskType.AUDIO_EDITING) return;
      audioEditingApi.get(msg.taskId)
        .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
        .catch(console.error);
    });

    return () => { unsubStatus(); unsubCompleted(); unsubFailed(); };
  }, [client, isConnected]);

  const handleSubmit = async (data: CreateAudioEditingRequest) => {
    try {
      const newItem = await audioEditingApi.create(data);
      setItems(prev => [newItem, ...prev]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
    } catch (error: any) {
      await alert(error.response?.data?.message || t('generation:toast.createFailed'), t('common:error'), 'danger');
      throw error;
    }
  };

  const handleReuseParams = (item: IAudioGeneration) => {
    setFormData({
      srcAudioUrl: item.spkAudioUrl ?? '',
      modelId: item.modelId,
      rvcModelId: item.rvcModelId ?? '',
      pitch: item.pitch ?? 0,
      indexRate: item.indexRate ?? 0.75,
      f0Method: '',
    });
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm(t('audioEdit.deleteConfirm'), t('common:confirmDelete'), 'danger');
    if (!confirmed) return;
    try {
      // 音频编辑暂无独立删除接口，直接从列表移除
      setItems(prev => prev.filter(i => i.id !== id));
      setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (error) {
      console.error('删除失败:', error);
    }
  };

  return (
    <div className="flex h-screen bg-bg-page">
      {/* 左侧表单 */}
      <div className="w-96 lg:w-[420px] bg-bg-form border-r border-border-subtle shadow-xl flex flex-col">
        <AudioTabs />
        <AudioEditingForm
          formData={formData}
          models={models}
          modelsLoading={modelsLoading}
          onFormDataChange={(data) => setFormData(prev => ({ ...prev, ...data }))}
          onSubmit={handleSubmit}
        />
      </div>

      {/* 右侧任务列表 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 标题 + 筛选 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-50">{t('audioEdit.title')}</h1>
              <p className="mt-1 text-sm text-gray-400">{t('audioEdit.subtitle')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('audioEdit.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    loadItems({ search: e.target.value }, 1);
                  }}
                  className="block w-full pl-9 pr-3 py-2 border border-border-subtle rounded-lg bg-bg-form placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-gray-50 text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  const v = e.target.value as TaskStatus | '';
                  setStatusFilter(v);
                  loadItems({ status: v || undefined }, 1);
                }}
                className="px-3 py-2 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
              >
                {statusOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 列表 */}
          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-gray-400">{t('common:loading')}</span>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 text-center">
              <MicrophoneIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">{t('audioEdit.empty')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('audioEdit.emptyHint')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map(item => (
                <AudioEditCard
                  key={item.id}
                  item={item}
                  onDelete={handleDelete}
                  onReuse={handleReuseParams}
                />
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-4 mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => loadItems({}, page, pagination.limit)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
